<template>
    <div class="ei-bridge-box">
        <div v-if="!boxData" class="ei-bridge-box-empty">분포를 표시할 데이터가 없습니다</div>
        <div v-else class="ei-bridge-box-row">
            <div class="ei-bridge-box-visual">
                <div ref="chartEl" class="ei-bridge-box-canvas"></div>
            </div>

            <div v-if="yearStats" class="ei-bridge-box-stats-wrap">
                <div class="ei-bridge-box-stats-head">
                    <span class="ei-bridge-box-stats-title">기준 통계</span>
                    <span class="ei-bridge-box-stats-sub">{{ okMonthLabel }}</span>
                </div>
                <table class="ei-bridge-box-stats" aria-label="원단위 분포 통계">
                    <tbody>
                        <tr class="ei-bbs-row--low">
                            <th scope="row" class="ei-bbs-label">최솟값</th>
                            <td class="ei-bbs-ym">{{ fmtYm(yearStats.minRegYm) }}</td>
                            <td class="ei-bbs-val">{{ fmtN(yearStats.minCalcVal) }}</td>
                        </tr>
                        <tr class="ei-bbs-row--high">
                            <th scope="row" class="ei-bbs-label">최댓값</th>
                            <td class="ei-bbs-ym">{{ fmtYm(yearStats.maxRegYm) }}</td>
                            <td class="ei-bbs-val">{{ fmtN(yearStats.maxCalcVal) }}</td>
                        </tr>
                        <tr>
                            <th scope="row" class="ei-bbs-label">평균</th>
                            <td class="ei-bbs-ym">{{ okMonthLabel }}</td>
                            <td class="ei-bbs-val">{{ fmtN(yearStats.avgCalcVal) }}</td>
                        </tr>
                        <tr class="ei-bbs-row--unit">
                            <th scope="row" class="ei-bbs-label">단위</th>
                            <td colspan="2" class="ei-bbs-unit">{{ intensityUnit || "—" }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import { enIntBridgeTooltipBase, enIntBridgeTooltipCardHtml } from "../utils/enIntUi.js";

const TEAL = "#0e7490";
const TEAL_SOFT = "rgba(14, 116, 144, 0.35)";
const MIN_DOT = "#94a3b8";
const MAX_DOT = "#1e293b";

function fmtVal(v, u) {
    const t = Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 });
    return u ? `${t} ${u}` : t;
}

function fiveNumberSummary(values) {
    const validValues = values.filter((value) => value != null && Number.isFinite(Number(value))).map(Number);
    if (validValues.length === 0) return null;
    validValues.sort((a, b) => a - b);
    const n = validValues.length;
    if (n === 1) return [validValues[0], validValues[0], validValues[0], validValues[0], validValues[0]];
    const min = validValues[0];
    const max = validValues[n - 1];
    const q = (p) => {
        const pos = (n - 1) * p;
        const lo = Math.floor(pos);
        const hi = Math.ceil(pos);
        if (lo === hi) return validValues[lo];
        return validValues[lo] + (pos - lo) * (validValues[hi] - validValues[lo]);
    };
    return [min, q(0.25), q(0.5), q(0.75), max];
}

export default {
    name: "EnIntBridgeBoxPlot",
    props: {
        getMasterSummaryApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        getMonthlySeriesApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        intensityUnit: { type: String, default: "" },
        /** 바 차트 호버 시 같은 스케일 y에 표시 */
        highlightY: { type: Number, default: null },
        highlightLabel: { type: String, default: "" },
    },
    data() {
        return { chart: null };
    },
    computed: {
        boxData() {
            const months = this.getMonthlySeriesApiState?.data?.months;
            if (!Array.isArray(months)) return null;
            const values = months.map((m) => m?.value);
            return fiveNumberSummary(values);
        },
        /** getMasterSummary.yearStats */
        yearStats() {
            return this.getMasterSummaryApiState?.data?.yearStats || null;
        },
        okMonthLabel() {
            if (!this.yearStats) return "";
            const n = this.yearStats.okMonthCount;
            return n != null ? `${n}개월` : "";
        },
    },
    watch: {
        getMasterSummaryApiState: { deep: true, handler: "scheduleRender" },
        getMonthlySeriesApiState: { deep: true, handler: "scheduleRender" },
        intensityUnit: "scheduleRender",
        highlightY: "scheduleRender",
        highlightLabel: "scheduleRender",
    },
    mounted() {
        this.scheduleRender();
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.onResize);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        scheduleRender() {
            this.$nextTick(() => {
                if (!this.boxData || this.boxData.length !== 5) {
                    if (this.chart) {
                        this.chart.dispose();
                        this.chart = null;
                    }
                    return;
                }
                this.renderOrUpdate();
            });
        },
        onResize() {
            if (this.chart) this.chart.resize();
        },
        yRangeWithHighlight(min, max) {
            const h = this.highlightY;
            let low = min;
            let high = max;
            if (h != null && Number.isFinite(h)) {
                low = Math.min(low, h);
                high = Math.max(high, h);
            }
            const span = high - low;
            const pad = span > 0 ? span * 0.08 : Math.max(Math.abs(high) * 0.04, 1e-6);
            return { yMin: low - pad, yMax: high + pad };
        },
        fmtN(v) {
            if (v == null) return "—";
            return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
        },
        fmtYm(ym) {
            if (!ym) return "";
            const s = String(ym);
            if (s.length === 6) return `${s.slice(0, 4)}.${s.slice(4)}`;
            return s;
        },
        renderOrUpdate() {
            const el = this.$refs.chartEl;
            if (!el) return;

            const w = el.clientWidth;
            const h = el.clientHeight;
            if (w < 8 || h < 8) {
                requestAnimationFrame(() => this.renderOrUpdate());
                return;
            }

            const [min, q1, q2, q3, max] = this.boxData;
            const { yMin, yMax } = this.yRangeWithHighlight(min, max);
            const highlight =
                this.highlightY != null && Number.isFinite(this.highlightY) ? Number(this.highlightY) : null;

            if (!this.chart) {
                this.chart = echarts.init(el, null, { renderer: "canvas" });
                window.addEventListener("resize", this.onResize);
            }

            const boxplotData = [[min, q1, q2, q3, max]];
            const u = this.intensityUnit || "";
            const markerVal = (v) => (u ? `${this.fmtN(v)} ${u}` : this.fmtN(v));

            const series = [
                {
                    name: "분포",
                    type: "boxplot",
                    data: boxplotData,
                    boxWidth: ["34%", "60%"],
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: "rgba(231, 240, 246, 0.98)" },
                            { offset: 0.45, color: "#c9d9e4" },
                            { offset: 1, color: "#b6c8d6" },
                        ]),
                        borderColor: "rgba(126, 149, 168, 0.8)",
                        borderWidth: 2,
                        borderType: "solid",
                        shadowColor: "rgba(100, 116, 139, 0.2)",
                        shadowBlur: 12,
                        shadowOffsetX: 0,
                        shadowOffsetY: 3,
                    },
                    emphasis: { disabled: true },
                },
                {
                    name: "최소",
                    type: "scatter",
                    data: [[0, min]],
                    symbol: "circle",
                    symbolSize: 9,
                    z: 2,
                    itemStyle: {
                        color: MIN_DOT,
                        borderColor: "rgba(148, 163, 184, 0.35)",
                        borderWidth: 2,
                        shadowColor: "rgba(148, 163, 184, 0.25)",
                        shadowBlur: 6,
                    },
                    label: {
                        show: true,
                        position: "bottom",
                        distance: 4,
                        align: "center",
                        formatter: () => `최소 ${markerVal(min)}`,
                        color: "#475569",
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 11,
                        backgroundColor: "rgba(255, 255, 255, 0.92)",
                        borderColor: "rgba(226, 232, 240, 0.95)",
                        borderWidth: 1,
                        borderRadius: 4,
                        padding: [2, 4],
                    },
                },
                {
                    name: "최대",
                    type: "scatter",
                    data: [[0, max]],
                    symbol: "circle",
                    symbolSize: 9,
                    z: 2,
                    itemStyle: {
                        color: MAX_DOT,
                        borderColor: "rgba(30, 41, 59, 0.25)",
                        borderWidth: 2,
                        shadowColor: "rgba(30, 41, 59, 0.2)",
                        shadowBlur: 6,
                    },
                    label: {
                        show: true,
                        position: "top",
                        distance: 4,
                        align: "center",
                        formatter: () => `최대 ${markerVal(max)}`,
                        color: "#0f172a",
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 11,
                        backgroundColor: "rgba(255, 255, 255, 0.92)",
                        borderColor: "rgba(226, 232, 240, 0.95)",
                        borderWidth: 1,
                        borderRadius: 4,
                        padding: [2, 4],
                    },
                },
            ];

            if (highlight != null) {
                series.push({
                    name: "선택",
                    type: "scatter",
                    data: [[0, highlight]],
                    symbol: "circle",
                    symbolSize: 10,
                    z: 12,
                    silent: false,
                    label: {
                        show: true,
                        position: "top",
                        distance: 1,
                        align: "center",
                        formatter: () => `${markerVal(highlight)}`,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 14,
                        backgroundColor: TEAL,
                        borderColor: "rgba(255, 255, 255, 0.35)",
                        borderWidth: 1,
                        borderRadius: 6,
                        padding: [5, 8],
                        shadowColor: TEAL_SOFT,
                        shadowBlur: 8,
                        shadowOffsetY: 1,
                    },
                    emphasis: { disabled: true },
                    itemStyle: {
                        color: TEAL,
                        borderColor: "#fff",
                        borderWidth: 2,
                        shadowColor: TEAL_SOFT,
                        shadowBlur: 10,
                        shadowOffsetY: 1,
                    },
                });
            }

            this.chart.setOption(
                {
                    animationDuration: 180,
                    tooltip: enIntBridgeTooltipBase({
                        appendToBody: true,
                        trigger: "item",
                        formatter: (p) => {
                            if (p.seriesType === "scatter") {
                                const v = p.value[1];
                                if (p.seriesName === "최소") {
                                    return enIntBridgeTooltipCardHtml({ heading: "최소", primary: fmtVal(v, u) });
                                }
                                if (p.seriesName === "최대") {
                                    return enIntBridgeTooltipCardHtml({ heading: "최대", primary: fmtVal(v, u) });
                                }
                                if (p.seriesName === "선택" && highlight != null) {
                                    return enIntBridgeTooltipCardHtml({
                                        cardClass: "ei-bridge-tooltip-card--box-focus",

                                        primary: fmtVal(highlight, u),
                                    });
                                }
                                return "";
                            }
                            const row = (label, num) => ({
                                label,
                                value: u ? `${num.toFixed(4)} ${u}` : num.toFixed(4),
                            });
                            return enIntBridgeTooltipCardHtml({
                                heading: "기간 분포",
                                rows: [
                                    row("최소", min),
                                    row("Q1", q1),
                                    row("중앙", q2),
                                    row("Q3", q3),
                                    row("최대", max),
                                ],
                            });
                        },
                    }),
                    grid: {
                        top: "13%",
                        bottom: "13%",
                    },
                    xAxis: {
                        type: "category",
                        data: [""],
                        show: false,
                    },
                    yAxis: {
                        type: "value",
                        min: yMin,
                        max: yMax,
                        scale: true,
                        show: false,
                    },
                    series,
                },
                true,
            );

            requestAnimationFrame(() => {
                this.chart?.resize();
            });
        },
    },
};
</script>

<style scoped>
.ei-bridge-box {
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    font-size: inherit;
    line-height: inherit;
}

.ei-bridge-box-row {
    display: grid;
    grid-template-columns: minmax(0, 1.7fr) minmax(188px, 1fr);
    align-items: stretch;
    gap: 10px;
    flex: 1 1 auto;
    min-height: 0;
}

.ei-bridge-box-visual {
    min-width: 0;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 6px 8px;
    border: 1px solid rgba(226, 232, 240, 0.92);
    border-radius: 10px;
    background:
        linear-gradient(180deg, rgba(248, 250, 252, 0.95), rgba(255, 255, 255, 0.98)),
        radial-gradient(circle at top left, rgba(14, 116, 144, 0.07), transparent 38%);
    box-shadow: 0 6px 14px rgba(148, 163, 184, 0.08);
}

.ei-bridge-box-canvas {
    width: 100%;
    flex: 1 1 auto;
    min-height: 124px;
}

.ei-bridge-box-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 86px;
    padding: 10px;
    font-size: var(--ei-bridge-font-subtle, 10px);
    font-weight: 600;
    color: #94a3b8;
    text-align: center;
    border: 1px dashed rgba(203, 213, 225, 0.95);
    border-radius: 10px;
    background: rgba(248, 250, 252, 0.8);
}

/* 통계 헤더·테이블은 부모 기본 폰트 상속 */
.ei-bridge-box-stats-wrap {
    --ei-bbs-border: rgba(148, 163, 184, 0.28);
    --ei-bbs-grid: rgba(226, 232, 240, 0.9);
    --ei-bbs-label: #64748b;
    --ei-bbs-date: #475569;
    --ei-bbs-value: #0f172a;
    --ei-bbs-low: #475569;
    --ei-bbs-high: #0f766e;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 0;
    font-size: inherit;
}

.ei-bridge-box-stats-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px;
    padding: 0 4px;
}

.ei-bridge-box-stats-title {
    font-size: var(--ei-bridge-font-title, 12px);
    font-weight: var(--ei-bridge-font-weight-title, 700);
    color: var(--ei-bridge-color-subtle, #475569);
}

.ei-bridge-box-stats-sub {
    font-size: var(--ei-bridge-font-subtle, 10px);
    font-weight: 700;
    color: #0e7490;
    padding: 1px 7px;
    border-radius: 999px;
    background: rgba(14, 116, 144, 0.1);
    white-space: nowrap;
}

.ei-bridge-box-stats {
    width: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    overflow: visible;
    box-shadow: none;
}

.ei-bbs-label {
    width: 36%;
    font-size: var(--ei-bridge-font-subtle, 10px);
    padding: 6px 10px;
    color: var(--ei-bbs-label);
    white-space: nowrap;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0.01em;
    text-align: left;
    background: linear-gradient(90deg, rgba(248, 250, 252, 0.88), rgba(248, 250, 252, 0.34));
}

.ei-bbs-ym {
    width: 30%;
    font-size: 10px;
    padding: 6px 4px;
    color: var(--ei-bbs-date);
    white-space: nowrap;
    font-weight: 600;
    letter-spacing: 0.01em;
    font-variant-numeric: tabular-nums;
    text-align: center;
}

.ei-bbs-val {
    width: 34%;
    font-size: 12px;
    padding: 6px 10px;
    font-weight: 800;
    color: var(--ei-bbs-value);
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    text-align: right;
}

.ei-bbs-row--low {
    background: linear-gradient(90deg, rgba(241, 245, 249, 0.68), rgba(255, 255, 255, 0));
}

.ei-bbs-row--high {
    background: linear-gradient(90deg, rgba(14, 116, 144, 0.08), rgba(255, 255, 255, 0));
}

.ei-bbs-unit {
    font-size: var(--ei-bridge-font-title, 11px);
    padding: 7px 10px;
    font-weight: 700;
    color: #334155;
    line-height: 1.3;
    word-break: break-word;
    background: linear-gradient(90deg, rgba(241, 245, 249, 0.8), rgba(255, 255, 255, 0.96));
    text-align: left;
}

.ei-bridge-box-stats tbody tr + tr th,
.ei-bridge-box-stats tbody tr + tr td {
    border-top: 1px solid var(--ei-bbs-grid);
}

.ei-bridge-box-stats tbody tr {
    transition: background-color 0.16s ease;
}

.ei-bridge-box-stats tbody tr:nth-child(odd):not(.ei-bbs-row--low):not(.ei-bbs-row--high) {
    background: rgba(248, 250, 252, 0.58);
}

.ei-bridge-box-stats tbody tr:hover {
    background: rgba(226, 240, 247, 0.5);
}

.ei-bbs-row--low .ei-bbs-val {
    color: var(--ei-bbs-low);
}

.ei-bbs-row--high .ei-bbs-label {
    color: #0e7490;
}

.ei-bbs-row--high .ei-bbs-val {
    color: var(--ei-bbs-high);
}

.ei-bbs-row--unit .ei-bbs-label {
    color: #64748b;
}

@media (max-width: 640px) {
    .ei-bridge-box-row {
        grid-template-columns: minmax(0, 1fr);
        gap: 8px;
    }

    .ei-bridge-box-visual,
    .ei-bridge-box-stats-wrap {
        height: auto;
    }

    .ei-bridge-box-canvas {
        min-height: 88px;
    }
}
</style>

<!-- appendToBody 툴팁 — 브리지 틸 톤 -->
<style>
.ei-bridge-echarts-tooltip .ei-bridge-tooltip-card--box-focus {
    border: 1px solid rgba(14, 116, 144, 0.28);
    border-left: 4px solid #0e7490;
    background: linear-gradient(165deg, rgba(14, 116, 144, 0.09) 0%, rgba(255, 255, 255, 0.99) 42%);
    box-shadow:
        0 10px 36px rgba(14, 116, 144, 0.12),
        0 2px 10px rgba(15, 23, 42, 0.06);
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-card--box-focus .ei-bridge-tooltip-heading {
    color: #0e7490;
    font-weight: 800;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-card--box-focus .ei-bridge-tooltip-primary {
    color: #0f766e;
    font-weight: 800;
}
</style>
